import * as Sentry from '@sentry/react';
import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { ContentCountCustomer } from './Box/ContentCountCustomer';
import { PageFiguresBox } from './Box/PageFiguresBox';
import { UpdateStatusBox } from './Box/UpdateStatusBox';
import { EntityStatus } from './Embed/EntityStatus/EntityStatus';
import { Optimize } from './Embed/Optimize/Optimize';
import { FlowForm } from './Embed/FlowForm';
import { Migrate } from './Embed/Migrate';
import { PullDashboard } from './Embed/PullDashboard/PullDashboard';
import { RegisteredSite } from './Embed/RegisteredSite';
import { RegisterSite } from './Embed/RegisterSite';
import { SyndicationDashboard } from './Embed/SyndicationDashboard';
import { NotFound } from './NotFound';
import { SiteSettings } from './Embed/SiteSettings';
import Notifications from './Embed/Notifications/Notifications';
import { SyncCoreFeatureFlagGate } from '../SyncCoreFeatureFlagGate';
import { FEATURE_WEBHOOKS_AVAILABLE } from '../../features';
import { Alert } from '@edgebox/react-components';
import { OnboardingConfiguration } from './Box/OnboardingConfiguration';
import { OnboardingSyndication } from './Box/OnboardingSyndication';
import { FormulaPlayground } from './Box/Formula/FormulaPlayground';
import { AiProvidersView } from './Embed/AI/screens/AiProvidersListScreen';
import { AiProviderDetailView } from './Embed/AI/screens/AiProviderDetailScreen';
import { AiModelsView } from './Embed/AI/screens/AiModelsListScreen';
import { AiModelDetailView } from './Embed/AI/screens/AiModelDetailScreen';
import { AiPromptTemplatesView } from './Embed/AI/screens/AiPromptTemplatesListScreen';
import { AiPromptTemplateDetailView } from './Embed/AI/screens/AiPromptTemplateDetailScreen';
import { AiExecutionsView } from './Embed/AI/screens/AiExecutionsListScreen';
import { AiPromptExecutionDetailView } from './Embed/AI/screens/AiExecutionDetailScreen';
import { CmsScreen } from '../CmsScreen';
import { RequireCurrentSite } from '../RequireCurrentSite';
import { GovernanceContentInventory } from './Governance/GovernanceContentInventory';
import { GovernanceIssues } from './Governance/GovernanceIssues';
import { GovernanceJobs } from './Governance/GovernanceJobs';
import { GovernanceOnboarding } from './Governance/GovernanceOnboarding';
import { GovernanceOnboardingStepKeyContent } from './Governance/GovernanceOnboardingStepKeyContent';
import { GovernanceOnboardingStepTaxonomies } from './Governance/GovernanceOnboardingStepTaxonomies';
import {
  GovernanceOnboardingStepTaxonomyTermsPrimary,
  GovernanceOnboardingStepTaxonomyTermsStrategic,
} from './Governance/GovernanceOnboardingStepTaxonomyTerms';
import { GovernanceOnboardingStepPrompts } from './Governance/GovernanceOnboardingStepPrompts';
import { GovernanceOnboardingStepReview } from './Governance/GovernanceOnboardingStepReview';
import { GovernanceCrawlingSettings } from './Governance/GovernanceCrawlingSettings';
import { GovernanceTaxonomyManagement } from './Governance/GovernanceTaxonomyManagement';
import { GovernanceMonitoringPrompts } from './Governance/GovernanceMonitoringPrompts';
import { BrandPresenceNavigation } from './Governance/BrandPresenceNavigation';
import { BRAND_PRESENCE_SITE_ISSUES_PATH, BrandPresenceSiteFrame } from './Governance/BrandPresenceSiteFrame';
import { GovernanceContentInventoryDetail } from './Governance/GovernanceContentInventoryDetail';
import { GovernanceOptimizeType } from './Governance/GovernanceOptimizeType';
import { GovernanceAnalyticsContentHealth } from './Governance/GovernanceAnalyticsContentHealth';
import { GovernanceAnalyticsAiVisibility } from './Governance/GovernanceAnalyticsAiVisibility';
import { GovernanceAnalyticsAiAnswers } from './Governance/GovernanceAnalyticsAiAnswers';
import { BrandPresenceOnboardingProvider } from './Governance/BrandPresenceOnboardingProvider';
import { BrandPresenceIndexRoute } from './Governance/BrandPresenceIndexRoute';
import {
  BRAND_PRESENCE_AI_VISIBILITY_PATH,
  BRAND_PRESENCE_AREA_ROUTE_PATHS,
  BRAND_PRESENCE_BASE_PATH,
  BRAND_PRESENCE_CONTENT_HEALTH_PATH,
  BRAND_PRESENCE_CRAWLING_SETTINGS_PATH,
  BRAND_PRESENCE_JOBS_PATH,
  BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS,
  BRAND_PRESENCE_ONBOARDING_START_PATH,
  BRAND_PRESENCE_TAXONOMIES_PATH,
  BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS,
} from './Governance/brandPresencePaths';

export function RoutingContainer() {
  const location = useLocation();
  const taxonomySuffix = location.pathname.startsWith('/settings/taxonomies')
    ? location.pathname.substring('/settings/taxonomies'.length)
    : '';
  const brandPresenceTaxonomySuffix = location.pathname.startsWith('/brand-presence/taxonomies')
    ? location.pathname.substring('/brand-presence/taxonomies'.length)
    : '';
  const taxonomyManagementSuffix = location.pathname.startsWith('/brand-presence/taxonomy-management')
    ? location.pathname.substring('/brand-presence/taxonomy-management'.length)
    : '';

  return (
    <>
      <div>
        <Sentry.ErrorBoundary>
          <Routes>
            <Route
              path="migrate"
              element={
                <RequireCurrentSite>
                  <Migrate />
                </RequireCurrentSite>
              }
            />
            <Route
              path="syndication-dashboard"
              element={
                <RequireCurrentSite>
                  <SyndicationDashboard />
                </RequireCurrentSite>
              }
            />
            <Route path="register-site" element={<RegisterSite />} />
            <Route
              path="site-registered"
              element={
                <RequireCurrentSite>
                  <RegisteredSite />
                </RequireCurrentSite>
              }
            />
            <Route
              path="site-settings"
              element={
                <RequireCurrentSite>
                  <SiteSettings />
                </RequireCurrentSite>
              }
            />
            <Route
              path="flow-form"
              element={
                <RequireCurrentSite>
                  <FlowForm />
                </RequireCurrentSite>
              }
            />

            <Route path="formula" element={<FormulaPlayground />} />

            <Route
              path={BRAND_PRESENCE_BASE_PATH.substring(1)}
              element={
                <BrandPresenceOnboardingProvider>
                  <BrandPresenceNavigation />
                </BrandPresenceOnboardingProvider>
              }
            >
              <Route index element={<BrandPresenceIndexRoute />} />
              <Route
                path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.entry}
                element={<Navigate to={`${BRAND_PRESENCE_ONBOARDING_START_PATH}${location.search}`} replace />}
              />
              <Route path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.domain} element={<GovernanceOnboarding />} />
              <Route path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.keyContent} element={<GovernanceOnboardingStepKeyContent />} />
              <Route path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepKeyContent} element={<GovernanceOnboardingStepKeyContent />} />
              <Route path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepTaxonomies} element={<GovernanceOnboardingStepTaxonomies />} />
              <Route
                path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepTaxonomyTermsPrimary}
                element={<GovernanceOnboardingStepTaxonomyTermsPrimary />}
              />
              <Route
                path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepTaxonomyTermsStrategic}
                element={<GovernanceOnboardingStepTaxonomyTermsStrategic />}
              />
              <Route path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepPrompts} element={<GovernanceOnboardingStepPrompts />} />
              <Route path={BRAND_PRESENCE_ONBOARDING_ROUTE_PATHS.stepReview} element={<GovernanceOnboardingStepReview />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.contentInventory} element={<GovernanceContentInventory />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.contentInventoryItem} element={<GovernanceContentInventoryDetail />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.contentInventoryItemStatus} element={<GovernanceContentInventoryDetail />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.issues} element={<GovernanceIssues />} />
              <Route
                path={BRAND_PRESENCE_AREA_ROUTE_PATHS.analytics}
                element={<Navigate to={BRAND_PRESENCE_AI_VISIBILITY_PATH} replace />}
              />
              <Route
                path={BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsContentHealth}
                element={<Navigate to={BRAND_PRESENCE_CONTENT_HEALTH_PATH} replace />}
              />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsContentHealthType} element={<GovernanceAnalyticsContentHealth />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsAiVisibility} element={<GovernanceAnalyticsAiVisibility />} />
              <Route
                path={BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsAiVisibilityType}
                element={<Navigate to={BRAND_PRESENCE_AI_VISIBILITY_PATH} replace />}
              />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsAiAnswers} element={<GovernanceAnalyticsAiAnswers />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.analyticsAiAnswersRun} element={<GovernanceAnalyticsAiAnswers />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.optimizeType} element={<GovernanceOptimizeType />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.optimizeTypeTaskGroup} element={<GovernanceOptimizeType />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.jobs} element={<Navigate to={BRAND_PRESENCE_JOBS_PATH} replace />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.jobGroups} element={<GovernanceJobs />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.jobTasks} element={<GovernanceJobs />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.jobGroup} element={<GovernanceJobs />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.jobTask} element={<GovernanceJobs />} />
              <Route
                path={BRAND_PRESENCE_AREA_ROUTE_PATHS.settings}
                element={<Navigate to={`${BRAND_PRESENCE_TAXONOMIES_PATH}${location.search}`} replace />}
              />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsTaxonomies} element={<GovernanceTaxonomyManagement />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsMonitoring} element={<GovernanceMonitoringPrompts />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsMonitoringPromptRuns} element={<GovernanceMonitoringPrompts />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsMonitoringPromptRun} element={<GovernanceMonitoringPrompts />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsMonitoringPromptRunNew} element={<GovernanceMonitoringPrompts />} />
              <Route path={BRAND_PRESENCE_AREA_ROUTE_PATHS.settingsCrawling} element={<GovernanceCrawlingSettings />} />
            </Route>

            <Route
              path={BRAND_PRESENCE_SITE_ISSUES_PATH.substring(1)}
              element={
                <RequireCurrentSite>
                  <CmsScreen>
                    <BrandPresenceSiteFrame />
                  </CmsScreen>
                </RequireCurrentSite>
              }
            >
              <Route index element={<GovernanceIssues />} />
            </Route>

            <Route path="settings/monitoring-prompts" element={<GovernanceMonitoringPrompts />} />
            <Route path="settings/monitoring-prompts/:promptId/runs" element={<GovernanceMonitoringPrompts />} />
            <Route path="settings/monitoring-prompts/:promptId/runs/:runId" element={<GovernanceMonitoringPrompts />} />
            <Route path="settings/monitoring-prompts/:promptId/run" element={<GovernanceMonitoringPrompts />} />
            <Route
              path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.settings}
              element={<Navigate to={`${BRAND_PRESENCE_TAXONOMIES_PATH}${location.search}`} replace />}
            />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.settingsMonitoringPromptRuns} element={<GovernanceMonitoringPrompts />} />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.settingsMonitoringPromptRun} element={<GovernanceMonitoringPrompts />} />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.settingsMonitoringPromptRunNew} element={<GovernanceMonitoringPrompts />} />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.settingsCrawling} element={<GovernanceCrawlingSettings />} />

            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.monitoringPromptRuns} element={<GovernanceMonitoringPrompts />} />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.monitoringPromptRun} element={<GovernanceMonitoringPrompts />} />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.monitoringPromptRunNew} element={<GovernanceMonitoringPrompts />} />

            <Route
              path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.legacyCrawlingSettings}
              element={<Navigate to={BRAND_PRESENCE_CRAWLING_SETTINGS_PATH} replace />}
            />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.legacyMonitoringPromptRuns} element={<GovernanceMonitoringPrompts />} />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.legacyMonitoringPromptRun} element={<GovernanceMonitoringPrompts />} />
            <Route path={BRAND_PRESENCE_TOP_LEVEL_ROUTE_PATHS.legacyMonitoringPromptRunNew} element={<GovernanceMonitoringPrompts />} />

            <Route path="ai/provider" element={<AiProvidersView />} />
            <Route path="ai/provider/:id" element={<AiProviderDetailView />} />
            <Route path="ai/model" element={<AiModelsView />} />
            <Route path="ai/model/:id" element={<AiModelDetailView />} />
            <Route path="ai/prompt-template" element={<AiPromptTemplatesView />} />
            <Route path="ai/prompt-template/:id" element={<AiPromptTemplateDetailView />} />
            <Route path="ai/prompt-execution" element={<AiExecutionsView />} />
            <Route path="ai/prompt-execution/:id" element={<AiPromptExecutionDetailView />} />

            <Route path="ai" element={<Navigate to="provider" replace />} />

            <Route
              path="entity-status"
              element={
                <RequireCurrentSite>
                  <EntityStatus />
                </RequireCurrentSite>
              }
            />
            <Route
              path="optimize"
              element={
                <RequireCurrentSite>
                  <CmsScreen>
                    <Optimize />
                  </CmsScreen>
                </RequireCurrentSite>
              }
            />
            <Route path="pull-dashboard" element={<PullDashboard />} />

            <Route
              path="Notifications"
              element={
                <RequireCurrentSite>
                  <SyncCoreFeatureFlagGate
                    featureName={FEATURE_WEBHOOKS_AVAILABLE}
                    ifEnabled={() => <Notifications />}
                    ifDisabled={() => <Alert variant="info">Please update your Sync Core to make use of notifications.</Alert>}
                  />
                </RequireCurrentSite>
              }
            />

            <Route path="box">
              <Route path="content-count/customer" element={<ContentCountCustomer />} />
              <Route path="onboarding/configuration/push" element={<OnboardingConfiguration type="push" />} />
              <Route path="onboarding/configuration/pull" element={<OnboardingConfiguration type="pull" />} />
              <Route path="onboarding/syndication/push" element={<OnboardingSyndication entityType="syndication" type="push" />} />
              <Route path="onboarding/syndication/pull" element={<OnboardingSyndication entityType="syndication" type="pull" />} />
              <Route path="onboarding/migration/push" element={<OnboardingSyndication entityType="migration" type="push" />} />
              <Route path="update-status" element={<UpdateStatusBox />} />
              <Route
                path="page-figures"
                element={
                  <CmsScreen>
                    <PageFiguresBox />
                  </CmsScreen>
                }
              />
            </Route>

            <Route element={<NotFound />} />
          </Routes>
        </Sentry.ErrorBoundary>
      </div>
    </>
  );
}
